import {
	Autocomplete,
	Box,
	CircularProgress,
	debounce,
	TextField,
	Typography
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import type { IB2B_list } from "src/content/modules/B2B/type/b2bType";
import { useGetQueryWithTotal } from "src/hooks/useGetQueryWithTotal";

interface B2BFilterSelectProps {
	value?: number | string | null;
	onChange: (b2bId: number | string | "") => void;
}

const B2BFilterSelect: React.FC<B2BFilterSelectProps> = ({ value, onChange }) => {
	const [querySearch, setQuerySearch] = useState("");
	const [selectedOption, setSelectedOption] = useState<IB2B_list | string | null>(
		value ? String(value) : null
	);

	useEffect(() => {
		if (!value) {
			setSelectedOption(null);
		}
	}, [value]);

	const debouncedSearch = useMemo(
		() =>
			debounce((val: string) => {
				setQuerySearch(val.trim());
			}, 300),
		[]
	);

	const { data, isLoading } = useGetQueryWithTotal<IB2B_list[]>({
		endPoint: "/api/fair/admin/b2b",
		queryKeys: ["B2B-ledger-filter", querySearch],
		params: {
			name: querySearch,
		},
	});

	const options: IB2B_list[] = useMemo(() => {
		const list: IB2B_list[] = Array.isArray(data?.data) ? [...data.data] : [];
		if (
			selectedOption &&
			typeof selectedOption !== "string" &&
			selectedOption.b2b_id
		) {
			const exists = list.some((opt) => opt.b2b_id === selectedOption.b2b_id);
			if (!exists) {
				list.unshift(selectedOption);
			}
		}
		return list;
	}, [data, selectedOption]);

	return (
		<Box sx={{ minWidth: { xs: "100%", sm: 220, md: 260 }, maxWidth: 320, flex: 1 }}>
			<Autocomplete<IB2B_list, false, false, true>
				freeSolo
				options={options}
				value={selectedOption || ""}
				loading={isLoading}
				getOptionLabel={(option: string | IB2B_list) => {
					if (!option) return "";
					if (typeof option === "string") return option;
					const org = option.b2b_organization
						? `${option.b2b_organization}`
						: "";
					return `${org}`;
				}}
				filterOptions={(opts, state) => {
					const input = state.inputValue.toLowerCase().trim();
					if (!input) return opts;
					return opts.filter((opt) => {
						const idStr = String(opt.b2b_id);
						const fullName = `${opt.b2b_first_name || ""} ${opt.b2b_last_name || ""
							}`.toLowerCase();
						const org = (opt.b2b_organization || "").toLowerCase();
						const phone = (opt.b2b_phone || "").toLowerCase();
						const email = (opt.b2b_email || "").toLowerCase();
						return (
							idStr.includes(input) ||
							fullName.includes(input) ||
							org.includes(input) ||
							phone.includes(input) ||
							email.includes(input)
						);
					});
				}}
				onInputChange={(_, newInputValue, reason) => {
					if (reason === "input") {
						debouncedSearch(newInputValue);
					} else if (reason === "clear") {
						debouncedSearch("");
						setSelectedOption(null);
						onChange("");
					}
				}}
				onChange={(_, newValue) => {
					setSelectedOption(newValue);
					if (!newValue) {
						onChange("");
					} else if (typeof newValue === "string") {
						onChange(newValue.trim());
					} else {
						onChange(newValue.b2b_id);
					}
				}}
				renderOption={(props, option) => {
					if (typeof option === "string") {
						return (
							<Box component="li" {...props} key={option}>
								{option}
							</Box>
						);
					}
					// const fullName = `${option.b2b_first_name || ""} ${option.b2b_last_name || ""
					// 	}`.trim();

					return (
						<Box
							component="li"
							{...props}
							key={option.b2b_id}
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								width: "100%",
								py: 0.75,
								px: 1.5,
								gap: 1,
							}}
						>
							<Box sx={{ minWidth: 0, flex: 1 }}>
								{/* <Typography variant="body2" fontWeight={600} noWrap>
									{fullName || "N/A"}
								</Typography> */}
								{option.b2b_organization && (
									<Typography
										variant="caption"
										color="text.secondary"
										noWrap
										display="block"
									>
										{option.b2b_organization}
									</Typography>
								)}
							</Box>

						</Box>
					);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						label="Filter by B2B"
						placeholder="Search B2B company name"
						size="small"
						InputLabelProps={{ shrink: true }}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={18} />
									) : null}
									{params.InputProps.endAdornment}
								</>
							),
						}}
					/>
				)}
			/>
		</Box>
	);
};

export default B2BFilterSelect;
